import React, { useContext, useEffect, useMemo, useState } from 'react'

import { connect } from 'react-redux'
import { getActiveLanguage, getTranslate } from 'react-localize-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { isNull, isUndefined } from 'lodash'

import { getUser } from '../../reducers/UserReducer'
import { EvaluationsContext } from '../../Providers/EvaluationsProvider'
import Loading from '../shared/Loading'
import { isObject } from '../../utils'
import { faFrown, faMeh, faSmile } from '@fortawesome/pro-regular-svg-icons'
import { Button } from 'antd'
import { saveEvaluation } from '../../utils/api/evaluation'
import { displayErrorMessage, onError, onSuccess } from '../../utils/apiHelper'
import { HttpStatuses } from '../../utils/http'

import '../../assets/recruitment-module.scss'

const mapStateToProps = state => {
  return {
    t: getTranslate(state.locale),
    user: getUser(state.getUser),
    activeLanguage: getActiveLanguage(state.locale).code
  }
}

const NEGATIVE_ANSWER = 'RED_FACE'
const NEUTRAL_ANSWER = 'ORANGE_FACE'
const POSITIVE_ANSWER = 'GREEN_FACE'

const StudentFeedback = ({ activeLanguage, internship, user, onSave, t }) => {
  const { getQuestions, getEvaluations, deleteEvaluations } = useContext(EvaluationsContext)

  const [loading, setLoading] = useState(true)
  const [parsedQuestions, setParsedQuestions] = useState([])
  const [comment, setComment] = useState('')
  const [submitError, setSubmitError] = useState(false)
  const [displayConfirmationMessage, setDisplayConfirmationMessage] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [studentAccessToken, setStudentAccessToken] = useState('')
  const [currentQuestions, setCurrentQuestions] = useState([])
  const [currentComment, setCurrentComment] = useState('')

  const language = useMemo(() => activeLanguage.split('_')[0], [activeLanguage])

  useEffect(() => {
    if (isObject(internship)) {
      setLoading(true)

      getQuestions(internship.institution).then(parseQuestions)
    }
  }, [internship])

  const loadQuestions = questions => {
    setCurrentQuestions(questions)
    setParsedQuestions(questions)
  }

  const loadComment = comment => {
    setCurrentComment(comment)
    setComment(comment)
  }

  const parseQuestions = questions => {
    const institutionQuestionTypes = []

    questions.forEach(item => {
      if (item.institution !== null) {
        institutionQuestionTypes.push(item.questionType)
      }
    })

    const parsedQuestions = questions.filter(item =>
      item.institution !== null ||
      !institutionQuestionTypes.includes(item.questionType)
    )
      .map(item => {
        const translationText = {}

        item.questions.forEach(questionText => {
          translationText[questionText.language] = questionText.value
        })

        return {
          id: item.id,
          questionType: item.questionType,
          isDefault: item.isDefault,
          texts: translationText,
          answer: null,
          invalid: false
        }
      })

    loadQuestions(parsedQuestions)
    loadEvaluation(parsedQuestions)
  }

  const loadEvaluation = (parsedQuestions) => {
    getEvaluations(internship).then(data => {
      if (data) {
        setStudentAccessToken(data.studentAccessToken)

        if (data.institutionEvaluationComment && data.institutionEvaluationComment.length) {
          loadComment(data.institutionEvaluationComment)
        }

        if (data.institutionEvaluationAnswers && data.institutionEvaluationAnswers.length) {
          const questionsUpdatedAnswers = parsedQuestions.map(question => {
            const answerForQuestion = data.institutionEvaluationAnswers.find(item => item.question.questionType === question.questionType)
            if (answerForQuestion) {
              question.answer = answerForQuestion.answer
            }

            return question
          })

          loadQuestions(questionsUpdatedAnswers)
        }
      }

      setLoading(false)
    })
  }

  const handleAnswerSelection = (questionId, answer) => {
    const questionsUpdatedAnswers = currentQuestions.map(question => {
      if (question.id === questionId) {
        question = { ...question }

        question.answer = answer
        question.invalid = false
      }

      return question
    })

    setCurrentQuestions(questionsUpdatedAnswers)

    if (submitError) {
      validateAnswers()
    }
  }

  const validateAnswers = () => {
    let isValid = true

    const checkedQuestions = currentQuestions.map(question => {
      if (!question.answer) {
        question.invalid = true
        isValid = false
      }

      return question
    })

    if (!isValid) {
      setCurrentQuestions(checkedQuestions)
      setSubmitError(true)
    } else if (submitError) {
      setSubmitError(false)
    }

    return isValid
  }

  const submitFeedback = () => {
    if (!validateAnswers()) {
      return
    }

    const dataToSend = {
      internshipId: internship.id,
      answers: currentQuestions.map(item => ({ questionId: item.id, questionType: item.questionType, answer: item.answer })),
      comment: currentComment.trim().length ? currentComment : null,
      studentAccessToken: studentAccessToken
    }

    setSubmitting(true)

    saveEvaluation(user, dataToSend).then(data => {
      if (data) {
        onSuccess(t('The evaluation has been sucessfully created'))
        deleteEvaluations(internship)
        setDisplayConfirmationMessage(true)

        if (typeof onSave === 'function') {
          onSave()
        }
      } else {
        onError(t('An error has occurred during the evaluation creation'))
      }
    }).catch(error => {
      if (error.status === HttpStatuses.FORBIDDEN) {
        displayErrorMessage(error, m => t(`student_feedback.${m}`))
      } else {
        onError(t('api.default_error_message'))
      }

      setCurrentComment(comment)
      setCurrentQuestions(parsedQuestions)
      setSubmitting(false)
    })
  }

  const evaluateDirtiness = () => {
    const areQuestionDirty = currentQuestions.reduce((acc, question) => {
      const originalQuestion = parsedQuestions.find(item => item.id === question.id)

      if (isUndefined(originalQuestion) || question.answer !== originalQuestion.answer) {
        acc = true
      }

      return acc
    }, false)

    return areQuestionDirty || comment !== currentComment
  }

  const isValid = currentQuestions.every(item => item.answer !== null)
  const isDirty = evaluateDirtiness()
  const isNew = parsedQuestions.every(item => isNull(item.answer))

  return (
    <>
      {!loading && (
        <div className='recruitment-module-wrapper'>
          <div className='info-text'>{t('student.feedback')} {internship?.sector.name} ?</div>
          <label>{t('Questions')}</label>
          <div className='questions-list'>
            {currentQuestions.map(item => (
              <div key={item.id} className={`question-item ${item.invalid ? 'invalid' : ''}`}>
                <div className='question-text'>{item.texts[language]}</div>
                <div className='question-options'>
                  <button
                    type='button'
                    className={item.answer === NEGATIVE_ANSWER ? 'negative-answer-selected' : ''}
                    onClick={() => { handleAnswerSelection(item.id, NEGATIVE_ANSWER) }}
                  >
                    <FontAwesomeIcon icon={faFrown} />
                  </button>
                  <button
                    type='button'
                    className={item.answer === NEUTRAL_ANSWER ? 'medium-answer-selected' : ''}
                    onClick={() => { handleAnswerSelection(item.id, NEUTRAL_ANSWER) }}
                  >
                    <FontAwesomeIcon icon={faMeh} />
                  </button>
                  <button
                    type='button'
                    className={item.answer === POSITIVE_ANSWER ? 'positive-answer-selected' : ''}
                    onClick={() => { handleAnswerSelection(item.id, POSITIVE_ANSWER) }}
                  >
                    <FontAwesomeIcon icon={faSmile} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className='comments-wrapper'>
            <label>{t('common.comments')}</label>
            <textarea
              value={currentComment}
              onChange={(e) => { setCurrentComment(e.target.value) }}
              placeholder={t("Comments about student's internship")}
            />
          </div>
          <div className={`submit-feedback-wrapper ${submitError ? 'space-between' : ''}`}>
            {submitError && <span className='submit-error'>{t('Please answer all questions')}</span>}
            {displayConfirmationMessage && <span className='previously-evaluated'>{t('Your feedback was recorded, thank you!')} </span>}
            {!displayConfirmationMessage && (isDirty || isNew) && (
              <Button
                disabled={!isDirty || !isValid}
                type='primary'
                htmlType='button'
                loading={submitting}
                onClick={submitFeedback}
              >
                <FontAwesomeIcon icon='check' />&nbsp;

                {isNew && t('common.confirm')}
                {!isNew && t('common.update')}
              </Button>
            )}
          </div>
        </div>
      )}
      {(loading) &&
        <Loading size='4x' />}
    </>
  )
}
export default connect(mapStateToProps)(StudentFeedback)
